/**
 * Главная полоса приложения.
 *
 * Слайды отсюда идут первыми, а дальше приложение по-прежнему собирает полосу
 * само — из ленты: ближайший розыгрыш, перенос срока, новые акции. Поэтому
 * здесь нельзя «выключить всё»: пустой слайдер не оставит человека без
 * первого экрана.
 *
 * Окно показа задаётся датами: «выключу вручную в понедельник» не работает ни
 * у кого.
 */
import type { Slide } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Skeleton, fmtShortDay } from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { useResource } from '../hooks';
import { LangTabs, setTr, tr, type ContentLang } from '../components/LangTabs';
import { useAdmin } from '../session';
import { useToast } from '../toast';

const ART_LABEL: Record<string, string> = {
  draw: 'кольца розыгрыша',
  moved: 'сдвиг даты',
  fresh: 'сетка новых акций',
  fair: 'дерево Меркла',
};

const EMPTY: Partial<Slide> = {
  position: 0,
  eyebrow: '',
  title: '',
  note: '',
  cta_text: '',
  href: '',
  art: 'fresh',
  regions: [],
  enabled: true,
};

export function SlidesScreen() {
  const data = useResource(() => api.adminSlides(), []);
  const { canModerate } = useAdmin();
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<Slide> | null>(null);

  const remove = async (slide: Slide) => {
    // Удаление необратимо, а кнопка стоит рядом с «Править»: спрашиваем.
    if (!window.confirm(`Удалить слайд «${slide.title}»? Это необратимо.`)) return;
    try {
      await api.adminDeleteSlide(slide.id);
      toast('Слайд удалён');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  const toggle = async (slide: Slide) => {
    try {
      await api.adminSaveSlide({ ...slide, enabled: !slide.enabled });
      toast(slide.enabled ? 'Слайд выключен' : 'Слайд включён');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  return (
    <Layout
      title="Главная полоса"
      subtitle="слайды оператора идут перед теми, что приложение собирает из ленты"
      action={
        canModerate ? (
          <Button variant="pri" icon="plus" onClick={() => setEditing({ ...EMPTY })}>
            Добавить слайд
          </Button>
        ) : undefined
      }
    >
      <ReadOnlyNote />

      {data.loading && <Skeleton count={3} height={64} />}
      {data.data && data.data.slides.length === 0 && (
        <Empty icon="flame" title="Своих слайдов нет">
          Полоса всё равно не пустая: приложение собирает её из ленты — ближайший розыгрыш,
          перенос срока, новые акции.
        </Empty>
      )}

      {data.data && data.data.slides.length > 0 && (
        <Card>
          {data.data.slides.map((slide) => (
            <div className={`slide ${slide.enabled ? '' : 'off'}`} key={slide.id}>
              <div className="pos">{slide.position}</div>
              <div style={{ minWidth: 0 }}>
                <div className="t">{slide.title}</div>
                {slide.note && <div className="n">{slide.note}</div>}
                <div className="meta">
                  {slide.eyebrow && <Badge>{slide.eyebrow}</Badge>}
                  <span>{ART_LABEL[slide.art] ?? slide.art}</span>
                  {slide.href && <span>→ {slide.href}</span>}
                  {slide.regions && slide.regions.length > 0 && (
                    <span>{slide.regions.join(' · ')}</span>
                  )}
                  {(slide.starts_at || slide.ends_at) && (
                    <span>
                      {slide.starts_at ? fmtShortDay(slide.starts_at) : '…'} —{' '}
                      {slide.ends_at ? fmtShortDay(slide.ends_at) : '…'}
                    </span>
                  )}
                  {!slide.enabled && <Badge tone="warn">выключен</Badge>}
                </div>
              </div>
              {canModerate && (
                <div className="row" style={{ gap: 6 }}>
                  <Button size="sm" onClick={() => void toggle(slide)}>
                    {slide.enabled ? 'Выключить' : 'Включить'}
                  </Button>
                  <Button size="sm" onClick={() => setEditing(slide)}>
                    Править
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => void remove(slide)}>
                    Удалить
                  </Button>
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {editing && (
        <SlideDialog
          slide={editing}
          arts={data.data?.arts ?? Object.keys(ART_LABEL)}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            data.reload();
          }}
        />
      )}
    </Layout>
  );
}

function SlideDialog({
  slide,
  arts,
  onClose,
  onDone,
}: {
  slide: Partial<Slide>;
  arts: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { me } = useAdmin();
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<Slide>>(slide);
  const [lang, setLang] = useState<ContentLang>('ru');
  const [busy, setBusy] = useState(false);

  const set = (patch: Partial<Slide>) => setDraft((d) => ({ ...d, ...patch }));

  // Текстовые поля слайда переводятся; настройки показа — нет.
  const text = (name: 'eyebrow' | 'title' | 'note' | 'cta_text') => ({
    value: tr(draft.i18n, lang, name, (draft[name] as string) ?? ''),
    onChange: (e: { target: { value: string } }) =>
      lang === 'ru'
        ? set({ [name]: e.target.value } as Partial<Slide>)
        : set({ i18n: setTr(draft.i18n, lang, name, e.target.value) }),
  });

  const submit = async () => {
    setBusy(true);
    try {
      await api.adminSaveSlide(draft);
      toast('Слайд сохранён');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  const toggleRegion = (code: string) => {
    const current = draft.regions ?? [];
    set({
      regions: current.includes(code) ? current.filter((r) => r !== code) : [...current, code],
    });
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 2 }}>
          <span className="h2">{draft.id ? 'Правка слайда' : 'Новый слайд'}</span>
          <span className="spacer" />
          <LangTabs lang={lang} onChange={setLang} i18n={draft.i18n} />
        </div>
        <p className="sub" style={{ margin: '6px 0 14px' }}>
          Заголовок — короткая фраза, которую человек прочитает за секунду. Ссылка открывается
          внутри приложения: путь вида «/c/ID» или «/my».
        </p>

        <div className="fgrid">
          <label className="field">
            <span className="lab">Метка сверху</span>
            <input
              className="input"
              value={draft.eyebrow ?? ''}
              placeholder="Например: Платформа"
              onChange={(e) => set({ eyebrow: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Порядок</span>
            <input
              className="input mono"
              type="number"
              value={draft.position ?? 0}
              onChange={(e) => set({ position: Number(e.target.value) })}
            />
          </label>
        </div>

        <label className="field">
          <span className="lab">Заголовок</span>
          <input
            className="input"
            {...text('title')}
          />
        </label>

        <label className="field">
          <span className="lab">Пояснение</span>
          <textarea
            className="input"
            rows={2}
            {...text('note')}
          />
        </label>

        <div className="fgrid">
          <label className="field">
            <span className="lab">Кнопка</span>
            <input
              className="input"
              value={draft.cta_text ?? ''}
              placeholder="Смотреть акцию"
              onChange={(e) => set({ cta_text: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Ссылка</span>
            <input
              className="input mono"
              value={draft.href ?? ''}
              placeholder="/c/01H…"
              onChange={(e) => set({ href: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Иллюстрация</span>
            <select
              className="select"
              value={draft.art ?? 'fresh'}
              onChange={(e) => set({ art: e.target.value })}
            >
              {arts.map((art) => (
                <option key={art} value={art}>
                  {ART_LABEL[art] ?? art}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="lab">Показывать</span>
            <select
              className="select"
              value={draft.enabled ? '1' : '0'}
              onChange={(e) => set({ enabled: e.target.value === '1' })}
            >
              <option value="1">да</option>
              <option value="0">нет</option>
            </select>
          </label>
          <label className="field">
            <span className="lab">Начало показа</span>
            <input
              className="input mono"
              type="date"
              value={dateValue(draft.starts_at)}
              onChange={(e) => set({ starts_at: dateISO(e.target.value) })}
            />
          </label>
          <label className="field">
            <span className="lab">Конец показа</span>
            <input
              className="input mono"
              type="date"
              value={dateValue(draft.ends_at)}
              onChange={(e) => set({ ends_at: dateISO(e.target.value, true) })}
            />
          </label>
        </div>

        <div className="field">
          <span className="lab">Страны — пусто значит «все»</span>
          <div className="chips" style={{ marginTop: 6 }}>
            {Object.entries(me?.regions ?? {}).map(([code, name]) => (
              <button
                key={code}
                type="button"
                className={`chip ${(draft.regions ?? []).includes(code) ? 'on' : ''}`}
                onClick={() => toggleRegion(code)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button variant="pri" disabled={busy || (draft.title ?? '').trim().length < 3} onClick={submit}>
            Сохранить
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Дата для <input type="date">: ISO-строка приходит с временем. */
function dateValue(iso?: string): string {
  return iso ? iso.slice(0, 10) : '';
}

/**
 * Дата из поля — в момент времени.
 *
 * Конец показа берётся концом дня: «до 20 октября» для человека означает
 * «включая двадцатое», а полночь оборвала бы слайд за сутки до ожидаемого.
 */
function dateISO(value: string, endOfDay = false): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}Z`).toISOString();
}
